/**
 * fitndex — Work Output Engine Stub
 * Replace this file with a real API call when the engine is ready.
 * The real endpoint receives exercise data and returns a work output score.
 */

/**
 * Calculate a mock work output score from exercise parameters.
 * Formula mirrors real physics: Work = Force × Distance (Joules equivalent).
 * This stub approximates the concept without the proprietary algorithm.
 *
 * @param {object} exercise - { sets, reps, weightLbs, durationSec, type }
 * @returns {number} score - work output in arbitrary units (WU)
 */
export function calculateWorkOutput(exercise) {
  const { sets = 1, reps = 0, weightLbs = 0, durationSec = 0, type = 'strength' } = exercise;

  let score = 0;

  if (type === 'strength' || type === 'mixed') {
    // Approximate: sets × reps × weight × displacement constant
    score += sets * reps * weightLbs * 0.42;
  }

  if (type === 'cardio' || type === 'mixed') {
    // Approximate: duration-based output
    score += durationSec * 1.8;
  }

  if (type === 'bodyweight') {
    score += sets * reps * 18;
  }

  return Math.round(score);
}

/**
 * Calculate total work output for an entire workout.
 * @param {object[]} exercises - array of exercise objects
 * @returns {{ total: number, breakdown: object[] }}
 */
export function calculateWorkoutOutput(exercises = []) {
  const breakdown = exercises.map((ex) => ({
    ...ex,
    workOutput: calculateWorkOutput(ex),
  }));

  const total = breakdown.reduce((sum, ex) => sum + (ex.workOutput || 0), 0);

  return { total, breakdown };
}

/**
 * Format a work output score for display.
 * @param {number} score
 * @returns {string} formatted label e.g. "1,240 WU"
 */
export function formatWorkOutput(score) {
  if (score >= 1000) {
    return `${(score / 1000).toFixed(1)}k WU`;
  }
  return `${score} WU`;
}

/**
 * Grade a work output score relative to a target.
 * @param {number} score
 * @param {number} target
 * @returns {'exceeded' | 'on-track' | 'below' | 'none'}
 */
export function gradeOutput(score, target) {
  if (!target) return 'none';
  const ratio = score / target;
  if (ratio >= 1.0) return 'exceeded';
  if (ratio >= 0.75) return 'on-track';
  return 'below';
}
